/**
 * IssueLogPanel.js
 * Inline panel component for viewing detailed data quality issues.
 * Replaces modal-based viewing with a dedicated tab experience.
 * 
 * Supports single or multiple issue groups (e.g., TextAnalysis has both similar + gibberish).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { 
  Search, Download, Filter, ChevronDown, ChevronUp, 
  Hash, FileText, User, Calendar, AlertTriangle, 
  Clock, Copy, XCircle, Type, ArrowUpDown, X
} from 'lucide-react';

const IssueLogPanel = ({
  // Single group (convenience)
  issues = [],
  title = 'Issue Details',
  issueType = 'outlier',
  totalCount = null,
  // Multiple groups
  issueGroups = null, // Array of { issues, title, issueType, totalCount }
}) => {
  const { isDarkMode } = useTheme();
  
  // Responsive
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 768;
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterVariable, setFilterVariable] = useState('all');
  const [filterCollector, setFilterCollector] = useState('all');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeGroup, setActiveGroup] = useState(0);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 50;

  // Normalize to groups format
  const groups = useMemo(() => {
    if (issueGroups && issueGroups.length > 0) {
      return issueGroups.filter(g => g.issues && g.issues.length > 0);
    }
    if (issues.length > 0) {
      return [{ issues, title, issueType, totalCount }];
    }
    return [];
  }, [issueGroups, issues, title, issueType, totalCount]);

  // Current group
  const currentGroup = groups[activeGroup] || groups[0] || { issues: [], title: '', issueType: 'outlier', totalCount: 0 };
  const currentIssues = currentGroup.issues || [];
  const realTotal = currentGroup.totalCount || currentIssues.length;

  // Colors
  const colors = {
    bg: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#1a1a1d' : '#FFFFFF',
    headerBg: isDarkMode ? '#141417' : '#F9FAFB',
    rowHover: isDarkMode ? '#222226' : '#F3F4F6',
    rowAlt: isDarkMode ? '#1e1e22' : '#FAFAFA',
    border: isDarkMode ? '#2D2E32' : '#E5E7EB',
    borderLight: isDarkMode ? '#252529' : '#F3F4F6',
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    primary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    
    issueStyles: {
      outlier: { color: '#F59E0B', bg: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)', label: 'Outliers' },
      duration: { color: '#F59E0B', bg: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)', label: 'Duration Issues' },
      duplicate: { color: '#8B5CF6', bg: isDarkMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.1)', label: 'Duplicates' },
      missing: { color: '#EF4444', bg: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)', label: 'Missing Values' },
      similar: { color: '#8B5CF6', bg: isDarkMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(139, 92, 246, 0.1)', label: 'Similar Responses' },
      gibberish: { color: '#F97316', bg: isDarkMode ? 'rgba(249, 115, 22, 0.15)' : 'rgba(249, 115, 22, 0.1)', label: 'Gibberish Responses' },
    }
  };

  const getIssueStyle = (type) => colors.issueStyles[type] || colors.issueStyles.outlier;
  const currentStyle = getIssueStyle(currentGroup.issueType);

  const getIcon = (type, size = 16) => {
    const iconColor = getIssueStyle(type).color;
    switch (type) {
      case 'duration': return <Clock size={size} color={iconColor} />;
      case 'duplicate': return <Copy size={size} color={iconColor} />;
      case 'missing': return <XCircle size={size} color={iconColor} />;
      case 'similar': return <Copy size={size} color={iconColor} />;
      case 'gibberish': return <Type size={size} color={iconColor} />;
      default: return <AlertTriangle size={size} color={iconColor} />;
    }
  };

  // Extract unique variables and collectors for filters
  const uniqueVariables = useMemo(() => {
    const vars = new Set();
    currentIssues.forEach(i => i.variable && vars.add(i.variable));
    return Array.from(vars).sort();
  }, [currentIssues]);

  const uniqueCollectors = useMemo(() => {
    const cols = new Set();
    currentIssues.forEach(i => i.collector_id && cols.add(String(i.collector_id)));
    return Array.from(cols).sort();
  }, [currentIssues]);

  // Filter and sort
  const filteredIssues = useMemo(() => {
    let result = [...currentIssues];

    // Search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(issue => 
        (issue.variable && issue.variable.toLowerCase().includes(q)) ||
        (issue.respondent_id && String(issue.respondent_id).toLowerCase().includes(q)) ||
        (issue.collector_id && String(issue.collector_id).toLowerCase().includes(q)) ||
        (issue.value !== null && issue.value !== undefined && String(issue.value).toLowerCase().includes(q)) ||
        (issue.row_index !== null && issue.row_index !== undefined && String(issue.row_index).includes(q))
      );
    }

    // Filter by variable
    if (filterVariable !== 'all') {
      result = result.filter(issue => issue.variable === filterVariable);
    }

    // Filter by collector
    if (filterCollector !== 'all') {
      result = result.filter(issue => String(issue.collector_id) === filterCollector);
    }

    // Sort
    if (sortField) {
      result.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [currentIssues, searchQuery, filterVariable, filterCollector, sortField, sortDirection]);

  // Reset page when filters/search/group change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterVariable, filterCollector, sortField, sortDirection, activeGroup]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredIssues.length / ITEMS_PER_PAGE));
  const paginatedIssues = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredIssues.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredIssues, currentPage]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // CSV Export
  const handleExport = () => {
    const headers = ['Row', 'Variable', 'Value', 'Respondent ID', 'Collector ID', 'Interview Date'];
    const csvRows = [headers.join(',')];
    filteredIssues.forEach(issue => {
      csvRows.push([
        issue.row_index ?? '',
        issue.variable ?? '',
        issue.value !== null && issue.value !== undefined ? `"${String(issue.value).replace(/"/g, '""')}"` : '',
        issue.respondent_id ?? '',
        issue.collector_id ?? '',
        issue.interview_date ?? ''
      ].join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `issue-log-${currentGroup.issueType}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setFilterVariable('all');
    setFilterCollector('all');
    setSortField(null);
    setSortDirection('asc');
  };

  const hasActiveFilters = searchQuery || filterVariable !== 'all' || filterCollector !== 'all';

  // Empty state
  if (groups.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '64px 32px',
        borderRadius: '16px',
        backgroundColor: colors.cardBg,
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '16px',
          backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <AlertTriangle size={28} color={colors.success} />
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
          No Issues Found
        </h3>
        <p style={{ fontSize: '13px', color: colors.textSecondary, maxWidth: '400px', margin: '0 auto' }}>
          Great news! No issues were detected in this analysis. Your data looks clean.
        </p>
      </div>
    );
  }

  // Sortable header cell
  const SortableHeader = ({ field, children, icon: Icon }) => {
    const isActive = sortField === field;
    return (
      <th
        onClick={() => handleSort(field)}
        style={{
          padding: '12px 16px',
          textAlign: 'left',
          fontWeight: '600',
          color: isActive ? colors.primary : colors.textSecondary,
          borderBottom: `1px solid ${colors.border}`,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'color 0.15s ease',
          whiteSpace: 'nowrap',
          backgroundColor: colors.headerBg,
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {Icon && <Icon size={12} />}
          {children}
          {isActive ? (
            sortDirection === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          ) : (
            <ArrowUpDown size={10} style={{ opacity: 0.3 }} />
          )}
        </div>
      </th>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* Group selector (only if multiple groups) */}
      {groups.length > 1 && (
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '4px',
          borderRadius: '10px',
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
          width: 'fit-content',
        }}>
          {groups.map((group, idx) => {
            const style = getIssueStyle(group.issueType);
            const isActive = idx === activeGroup;
            return (
              <button
                key={idx}
                onClick={() => { setActiveGroup(idx); resetFilters(); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  backgroundColor: isActive ? style.bg : 'transparent',
                  color: isActive ? style.color : colors.textSecondary,
                  fontSize: '13px',
                  fontWeight: isActive ? '600' : '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {getIcon(group.issueType, 14)}
                {group.title}
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: '600',
                  backgroundColor: isActive ? `${style.color}20` : (isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'),
                  color: isActive ? style.color : colors.textMuted,
                }}>
                  {(group.totalCount || group.issues.length).toLocaleString()}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Stats bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 16px',
        borderRadius: '12px',
        backgroundColor: colors.cardBg,
        border: `1px solid ${colors.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px',
            backgroundColor: currentStyle.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {getIcon(currentGroup.issueType, 20)}
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
              {groups.length <= 1 ? currentGroup.title : getIssueStyle(currentGroup.issueType).label}
            </div>
            <div style={{ fontSize: '13px', color: colors.textSecondary }}>
              {realTotal.toLocaleString()} total records
              {hasActiveFilters && ` • ${filteredIssues.length} matching filters`}
            </div>
          </div>
        </div>

        <button
          onClick={handleExport}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: 'transparent',
            color: colors.textSecondary,
            fontSize: '13px', fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.primary;
            e.currentTarget.style.color = colors.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = colors.border;
            e.currentTarget.style.color = colors.textSecondary;
          }}
        >
          <Download size={14} />
          Export CSV
        </button>
      </div>

      {/* Search & Filters */}
      <div style={{
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1', minWidth: '220px' }}>
          <Search size={14} style={{
            position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
            color: colors.textMuted, pointerEvents: 'none',
          }} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by variable, respondent, value..."
            style={{
              width: '100%', padding: '9px 12px 9px 36px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.cardBg,
              color: colors.text,
              fontSize: '13px',
              outline: 'none',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => e.target.style.borderColor = colors.primary}
            onBlur={(e) => e.target.style.borderColor = colors.border}
          />
        </div>

        {/* Variable filter */}
        {uniqueVariables.length > 1 && (
          <div style={{ position: 'relative' }}>
            <Filter size={12} style={{
              position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
              color: colors.textMuted, pointerEvents: 'none',
            }} />
            <select
              value={filterVariable}
              onChange={(e) => setFilterVariable(e.target.value)}
              style={{
                padding: '9px 32px 9px 30px',
                borderRadius: '8px',
                border: `1px solid ${filterVariable !== 'all' ? colors.primary : colors.border}`,
                backgroundColor: colors.cardBg,
                color: colors.text,
                fontSize: '13px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 8px center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '16px',
                outline: 'none',
              }}
            >
              <option value="all">All Variables</option>
              {uniqueVariables.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        )}

        {/* Collector filter */}
        {uniqueCollectors.length > 1 && (
          <div style={{ position: 'relative' }}>
            <User size={12} style={{
              position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)',
              color: colors.textMuted, pointerEvents: 'none',
            }} />
            <select
              value={filterCollector}
              onChange={(e) => setFilterCollector(e.target.value)}
              style={{
                padding: '9px 32px 9px 30px',
                borderRadius: '8px',
                border: `1px solid ${filterCollector !== 'all' ? colors.primary : colors.border}`,
                backgroundColor: colors.cardBg,
                color: colors.text,
                fontSize: '13px',
                cursor: 'pointer',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 8px center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '16px',
                outline: 'none',
              }}
            >
              <option value="all">All Collectors</option>
              {uniqueCollectors.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        {/* Clear filters */}
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '8px 12px', borderRadius: '8px',
              border: 'none',
              backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
              color: colors.error,
              fontSize: '12px', fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <X size={12} />
            Clear
          </button>
        )}
      </div>

      {/* Table */}
      <div style={{
        borderRadius: '12px',
        backgroundColor: colors.cardBg,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}>
        <div style={{
          maxHeight: 'calc(100vh - 340px)',
          minHeight: '400px',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: isDarkMode ? '#3D3E42 transparent' : '#D1D5DB transparent',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <SortableHeader field="row_index" icon={Hash}>Row</SortableHeader>
                <SortableHeader field="variable" icon={FileText}>Variable</SortableHeader>
                <SortableHeader field="value">Value</SortableHeader>
                <SortableHeader field="respondent_id" icon={User}>Respondent</SortableHeader>
                <SortableHeader field="collector_id">Collector</SortableHeader>
                <SortableHeader field="interview_date" icon={Calendar}>Date</SortableHeader>
              </tr>
            </thead>
            <tbody>
              {filteredIssues.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{
                    padding: '48px 16px',
                    textAlign: 'center',
                    color: colors.textMuted,
                    fontSize: '14px',
                  }}>
                    {hasActiveFilters
                      ? 'No issues match your current filters.'
                      : 'No issues to display.'
                    }
                  </td>
                </tr>
              ) : (
                paginatedIssues.map((issue, idx) => (
                  <tr
                    key={idx}
                    style={{
                      backgroundColor: idx % 2 === 0 ? 'transparent' : colors.rowAlt,
                      transition: 'background-color 0.15s ease',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = colors.rowHover}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? 'transparent' : colors.rowAlt}
                  >
                    <td style={{
                      padding: '11px 16px',
                      color: colors.textMuted,
                      borderBottom: `1px solid ${colors.borderLight}`,
                      fontFamily: '"SF Mono", "Fira Code", monospace',
                      fontSize: '12px',
                    }}>
                      {issue.row_index ?? '-'}
                    </td>
                    <td style={{
                      padding: '11px 16px',
                      color: colors.text,
                      borderBottom: `1px solid ${colors.borderLight}`,
                      fontWeight: '500',
                    }}>
                      <span style={{
                        backgroundColor: isDarkMode ? '#2D2E32' : '#F3F4F6',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                      }}>
                        {issue.variable || '-'}
                      </span>
                    </td>
                    <td style={{
                      padding: '11px 16px',
                      borderBottom: `1px solid ${colors.borderLight}`,
                      fontFamily: '"SF Mono", "Fira Code", monospace',
                      fontSize: '12px',
                      maxWidth: '150px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {issue.value !== null && issue.value !== undefined ? (
                        <span style={{
                          color: currentStyle.color,
                          backgroundColor: currentStyle.bg,
                          padding: '2px 8px',
                          borderRadius: '4px',
                        }}>
                          {typeof issue.value === 'number'
                            ? issue.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                            : String(issue.value)}
                        </span>
                      ) : (
                        <span style={{
                          color: currentGroup.issueType === 'missing' ? colors.error : colors.textMuted,
                          fontStyle: currentGroup.issueType === 'missing' ? 'italic' : 'normal',
                        }}>
                          {currentGroup.issueType === 'missing' ? 'Missing' : '-'}
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: '11px 16px',
                      color: colors.text,
                      borderBottom: `1px solid ${colors.borderLight}`,
                      fontSize: '13px',
                    }}>
                      {issue.respondent_id || '-'}
                    </td>
                    <td style={{
                      padding: '11px 16px',
                      color: colors.textSecondary,
                      borderBottom: `1px solid ${colors.borderLight}`,
                      fontSize: '13px',
                    }}>
                      {issue.collector_id || '-'}
                    </td>
                    <td style={{
                      padding: '11px 16px',
                      color: colors.textMuted,
                      borderBottom: `1px solid ${colors.borderLight}`,
                      fontSize: '12px',
                    }}>
                      {issue.interview_date || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer with pagination */}
        <div style={{
          padding: '12px 20px',
          backgroundColor: colors.headerBg,
          borderTop: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
        }}>
          <span style={{ fontSize: '12px', color: colors.textMuted }}>
            {hasActiveFilters
              ? `${filteredIssues.length} of ${currentIssues.length} issues`
              : `${currentIssues.length} issues`
            }
            {filteredIssues.length > ITEMS_PER_PAGE && (
              <span style={{ marginLeft: '6px' }}>
                (page {currentPage} of {totalPages})
              </span>
            )}
          </span>
          
          {/* Pagination buttons */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: currentPage === 1 ? colors.textMuted : colors.text,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                }}
              >First</button>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: currentPage === 1 ? colors.textMuted : colors.text,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  opacity: currentPage === 1 ? 0.5 : 1,
                }}
              >←</button>
              <span style={{ padding: '0 8px', fontSize: '12px', color: colors.textSecondary, fontWeight: '500' }}>
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: currentPage === totalPages ? colors.textMuted : colors.text,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                }}
              >→</button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '4px 8px',
                  fontSize: '11px',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: currentPage === totalPages ? colors.textMuted : colors.text,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  opacity: currentPage === totalPages ? 0.5 : 1,
                }}
              >Last</button>
            </div>
          )}
          
          {uniqueVariables.length > 0 && (
            <span style={{ fontSize: '12px', color: colors.textMuted }}>
              {uniqueVariables.length} variable{uniqueVariables.length !== 1 ? 's' : ''} • {uniqueCollectors.length} collector{uniqueCollectors.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default IssueLogPanel;